import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Plus, Check, Package2, AlertCircle, ChevronDown, ChevronRight, Minus } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { ProductPhotoService } from '../../services/productPhotoService';

interface StockItem {
  id: string;
  name: string;
  acquisition_cost: number;
  price: number;
  quantity: number;
  category_id: string;
  category_name?: string;
  description?: string;
  track_inventory?: boolean;
  allow_oversell?: boolean;
}

interface StockCategory {
  id: string;
  name: string;
  items?: StockItem[];
}

interface SelectInventoryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (product: StockItem) => Promise<void>;
  onUpdateQuantity?: (product: StockItem, newQuantity: number) => Promise<void>;
  currentBookingProducts?: Array<{
    product: {
      id: string;
      name: string;
      quantity: number;
    };
  }>;
}

export default function SelectInventoryItemModal({ 
  isOpen, 
  onClose,
  onAdd,
  onUpdateQuantity,
  currentBookingProducts = []
}: SelectInventoryItemModalProps) {
  
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const loadData = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch stock items with categories
      const { data: items, error: itemsError } = await supabase
        .from('stock_items')
        .select(`
          id,
          name,
          acquisition_cost,
          price,
          quantity,
          category_id,
          description,
          is_one_time,
          is_deleted,
          track_inventory,
          allow_oversell,
          stock_categories!inner(name)
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false) // Exclude deleted items
        .order('name');

      if (itemsError) throw itemsError;

      // Transform the data to include category names and filter out one-time items with 0 quantity
      const transformedItems: StockItem[] = (items || [])
        .filter(item => {
          // Exclude one-time items that have reached 0 quantity
          if (item.is_one_time && item.quantity === 0) {
            return false;
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          name: item.name,
          acquisition_cost: item.acquisition_cost || 0,
          price: item.price || 0,
          quantity: item.quantity || 0,
          category_id: item.category_id,
          category_name: item.stock_categories?.name || 'Uncategorized',
          description: item.description,
          track_inventory: item.track_inventory !== false,
          allow_oversell: item.allow_oversell === true
        }));

      setStockItems(transformedItems);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('stock_categories')
        .select('id, name')
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Group items by category
      const categoriesWithItems: StockCategory[] = (categoriesData || []).map(category => ({
        id: category.id,
        name: category.name,
        items: transformedItems.filter(item => item.category_id === category.id)
      }));

      setCategories(categoriesWithItems);

      // Load product photos
      const itemIds = transformedItems.map(item => item.id);
      if (itemIds.length > 0) {
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(itemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          Object.entries(photoMap).forEach(([itemId, photo]) => {
            photoUrls[itemId] = photo.photo_url;
          });
          
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.error('Error loading product photos:', photoError);
          // Don't fail the whole load if photos fail
        }
      }

    } catch (err) {
      console.error('Error loading inventory data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      setIsAnimating(true);
      // Clear selected items when modal opens to reflect current booking state
      setSelectedItems(new Set());
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, selectedBusiness?.id]);

  const filteredCategories = categories.map(category => ({
    ...category,
    items: category.items?.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []
  })).filter(category => category.items.length > 0);

  // Get current booking quantity for an item
  const getCurrentBookingQuantity = (itemId: string): number => {
    const bookingProduct = currentBookingProducts.find(p => p.product.id === itemId);
    return bookingProduct?.product.quantity || 0;
  };

  const handleAddItem = async (item: StockItem) => {
    // Prevent adding out-of-stock items (untracked and oversellable items are always available)
    if (item.track_inventory !== false && item.allow_oversell !== true && item.quantity <= 0) return;
    
    try {
      await onAdd(item);
      setSelectedItems(prev => new Set(prev).add(item.id));
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleUpdateQuantity = async (item: StockItem, newQuantity: number) => {
    if (!onUpdateQuantity) return;
    
    try {
      await onUpdateQuantity(item, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Component for item icon - shows photo if available, otherwise blue icon
  const ItemIcon = ({ item }: { item: StockItem }) => {
    const photoUrl = productPhotos[item.id];
    
    if (photoUrl) {
      return (
        <div className="h-10 w-10 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-5 w-5 text-blue-600"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-5 w-5" />
      </div>
    );
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-300 z-[100025] ${
        isOpen ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={onClose}
      style={{
        zIndex: 100025,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div 
        className={`fixed right-0 w-full md:w-[480px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex flex-col h-full bg-white">
          {/* Header - iOS clean */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package2 className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">Select Product</h2>
                <p className="text-xs text-gray-500 mt-0.5">Add inventory items to this offer</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
              style={{ minWidth: 44, minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search - mobile optimized */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-100 bg-gray-50/50">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                style={{ fontSize: 16, minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
              />
            </div>
          </div>

          {/* Content - scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50/30" style={{ WebkitOverflowScrolling: 'touch' }}>
            {error && (
              <div className="m-4 sm:m-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-700">{error}</p>
                    <button
                      type="button"
                      onClick={loadData}
                      className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-colors touch-manipulation"
                      style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center min-h-[200px] sm:min-h-[280px]">
                <div className="text-center">
                  <div className="w-10 h-10 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
                  <p className="mt-4 text-sm text-gray-500">Loading products...</p>
                </div>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center px-6 py-12">
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Package2 className="h-7 w-7 text-gray-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {searchQuery ? 'No matching products' : 'No products yet'}
                </h3>
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'Try a different search' : 'Add products in Inventory to use here'}
                </p>
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-3">
                {filteredCategories.map(category => (
                  <div key={category.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => toggleCategoryExpansion(category.id)}
                      className="w-full px-4 py-3.5 flex items-center justify-between bg-gray-50/80 hover:bg-gray-100 active:bg-gray-100 transition-colors touch-manipulation text-left"
                      style={{ minHeight: 48, WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Package2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{category.name}</span>
                        <span className="text-xs text-gray-500 flex-shrink-0">({category.items?.length || 0})</span>
                      </div>
                      {expandedCategories.has(category.id) ? (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>

                    {expandedCategories.has(category.id) && (
                      <div className="border-t border-gray-100 divide-y divide-gray-100">
                        {category.items?.map(item => {
                          const isSelected = selectedItems.has(item.id);
                          const isTracked = item.track_inventory !== false && item.allow_oversell !== true;
                          const isOutOfStock = isTracked && item.quantity <= 0;
                          const currentBookingQty = getCurrentBookingQuantity(item.id);
                          const isInBooking = currentBookingQty > 0;
                          const availableStock = isTracked ? item.quantity - currentBookingQty : Infinity;

                          return (
                            <div key={item.id} className="px-4 py-3 sm:py-4 bg-white">
                              <div className="flex items-center gap-3">
                                <ItemIcon item={item} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className={`font-medium truncate ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                                      {item.name}
                                    </h4>
                                    {isOutOfStock && (
                                      <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg flex-shrink-0">
                                        Out of stock
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className={`text-sm mt-0.5 truncate ${isOutOfStock ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {item.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                    <span>${item.price.toFixed(2)}</span>
                                    <span className={isTracked && availableStock <= 5 ? 'text-amber-600 font-medium' : ''}>
                                      {item.track_inventory === false
                                        ? 'Made to order'
                                        : isTracked
                                          ? `${availableStock} left`
                                          : `${item.quantity - currentBookingQty} left`}
                                    </span>
                                    {isInBooking && (
                                      <span className="text-blue-600 font-medium">{currentBookingQty} in offer</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-base font-semibold tabular-nums ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                                    ${item.price.toFixed(2)}
                                  </span>
                                  {isInBooking ? (
                                    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateQuantity(item, Math.max(0, currentBookingQty - 1))}
                                        className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-200 active:scale-95 transition-all touch-manipulation"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        <Minus className="h-3.5 w-3.5" />
                                      </button>
                                      <span className="w-6 text-center text-sm font-medium">{currentBookingQty}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateQuantity(item, currentBookingQty + 1)}
                                        disabled={availableStock <= 0}
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all touch-manipulation disabled:opacity-50 ${
                                          availableStock <= 0 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-50 active:scale-95'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        <Plus className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleAddItem(item)}
                                      disabled={isSelected || isOutOfStock || availableStock <= 0}
                                      className={`min-h-[44px] px-4 rounded-xl text-sm font-medium transition-all touch-manipulation flex items-center gap-1.5 ${
                                        isSelected
                                          ? 'bg-green-100 text-green-700'
                                          : isOutOfStock || availableStock <= 0
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white'
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      {isSelected ? (
                                        <>
                                          <Check className="h-4 w-4" />
                                          Added
                                        </>
                                      ) : (
                                        'Add'
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(modalContent, document.body);
}
